import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ConnectPolkadotWalletModal from '@/shared/components/ConnectPolkadotWalletModal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { OperatorIdPageContent } from '@/shared/components/operators/OperatorIdPageContent';
import useBoolean from '@/shared/hooks/useBoolean';
import useDelegateFlip from '@/shared/hooks/useDelegateFlip';
import useEvmScAccount from '@/shared/hooks/useEvmScAccount';
import useOperator from '@/shared/hooks/useOperator';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import useUndelegateFlip from '@/shared/hooks/useUndelegateFlip';
import MinusCircleIcon from '@/shared/icons/flip-ui-kit/large/MinusCircle';
import PlusCircleIcon from '@/shared/icons/flip-ui-kit/large/PlusCircle';
import SettingsIcon from '@/shared/icons/flip-ui-kit/large/Settings';
import ArrowBackIcon from '@/shared/icons/flip-ui-kit/small/ArrowBack';
import TokenAmount from '@/shared/utils/TokenAmount';
import AddToListModal from './AddToListModal';
import ClaimValidatorModal from './ClaimValidatorModal';
import { RegisterNodeModal } from './RegisterNodeModal';
import SettingsModal from './SettingsModal';
import OperatorNetwork from '../../../assets/svg/operator-network.svg';
import useOperatorAccount from '../../../hooks/useOperatorAccount';
import useSettingsModal from '../../../hooks/useSettingsModal';
import { useExtrinsicMutation, useStateChainAccount } from '../../../hooks/useStateChainAccount';
import useStore from '../../../hooks/useStore';
import ValidatorRedemptionModal from '../RedemptionModal/ValidatorRedemptionModal';

const DelegationActions = ({ operatorIdSs58 }: { operatorIdSs58: `cF${string}` }) => {
  const router = useRouter();
  const { nextEpoch, idSs58: delegatorIdSs58 } = useEvmScAccount(true);

  const { exceptionList, openDelegationEnabled, delegators } = useOperator(operatorIdSs58);

  const { mutate: undelegate, isPending: undelegatePending } = useUndelegateFlip();
  const { mutate: delegate, isPending: delegatePending } = useDelegateFlip();

  return (
    <>
      <Button
        icon={<MinusCircleIcon className="mr-1 group-enabled:text-cf-red-2" />}
        type="secondary-standard"
        iconPos="left"
        onClick={() => undelegate(undefined)}
        disabled={!delegators.has(delegatorIdSs58) || 'ifLoading'}
        loading={undelegatePending && 'iconOnly'}
      >
        Remove funds
      </Button>
      <Button
        icon={<PlusCircleIcon className="mr-1" />}
        iconPos="left"
        onClick={() => {
          if (nextEpoch.delegatedBalance.gt(0)) {
            delegate({ operator: operatorIdSs58, amount: TokenAmount.ZERO });
          } else {
            router.push(`/delegate?operator=${operatorIdSs58}`);
          }
        }}
        disabled={
          (!openDelegationEnabled && !exceptionList.includes(delegatorIdSs58)) ||
          delegators.has(delegatorIdSs58) ||
          'ifLoading'
        }
        loading={delegatePending && 'iconOnly'}
      >
        Delegate funds
      </Button>
    </>
  );
};

export default function OperatorContent({
  idSs58,
  backPath,
}: {
  idSs58?: `cF${string}`;
  backPath?: string;
}) {
  const { show: openSettingsModal } = useSettingsModal();
  const { selectedAccount: polkadotAccount } = usePolkadot();
  const savePolkadotAccount = useStore((state) => state.savePolkadotAccount);

  const { accountInfo } = useStateChainAccount();

  const {
    value: claimModalOpen,
    setTrue: openClaimModal,
    setFalse: closeClaimModal,
  } = useBoolean(false);
  const {
    value: redeemModalOpen,
    setTrue: openRedeemModal,
    setFalse: closeRedeemModal,
  } = useBoolean(false);
  const {
    value: fundModalOpen,
    setTrue: openFundModal,
    setFalse: closeFundModal,
  } = useBoolean(false);

  const {
    value: accountModalOpen,
    setTrue: openAccountModal,
    setFalse: closeAccountModal,
  } = useBoolean(false);

  const {
    value: addToListModalOpen,
    setTrue: openAddToListModal,
    setFalse: closeAddToListModal,
  } = useBoolean(false);

  const operator = useOperatorAccount(idSs58);

  const account = useEvmScAccount();

  const [redemptionAccount, setRedemptionAccount] = useState<`cF${string}`>();
  const [fundingAccount, setFundingAccount] = useState<`cF${string}`>();

  const shouldShowGetStarted = idSs58 === undefined && accountInfo?.role !== 'operator';
  const operatorId = (idSs58 ?? operator?.id) as `cF${string}`;

  const toast = useToast('operator-content');
  const blockDelegator = useExtrinsicMutation('operator_blockDelegator', {
    onMutate() {
      toast.loading('Submitting transaction to the State Chain...');
    },
    onSuccess() {
      toast.success('Successfully blacklisted delegator');
    },
    onError(err) {
      toast.error('Error while submitting transaction:', {
        description: err.message,
      });
    },
  });
  const allowDelegator = useExtrinsicMutation('operator_allowDelegator', {
    onMutate() {
      toast.loading('Submitting transaction to the State Chain...');
    },
    onSuccess() {
      toast.success('Successfully whitelisted delegator');
    },
    onError(err) {
      toast.error('Error while submitting transaction:', {
        description: err.message,
      });
    },
  });

  let actions;

  if (operator) {
    actions = (
      <>
        <Button
          icon={<SettingsIcon className="mr-1" />}
          type="secondary-standard"
          iconPos="left"
          onClick={() => {
            openSettingsModal();
          }}
        >
          Settings
        </Button>
        <Button icon={<PlusCircleIcon className="mr-1" />} iconPos="left" onClick={openClaimModal}>
          Claim Validator
        </Button>
      </>
    );
  } else if (account.connected) {
    actions = <DelegationActions operatorIdSs58={operatorId} />;
  }
  return (
    <>
      {shouldShowGetStarted ? (
        <div className="flex flex-col items-center gap-y-6 text-center font-aeonikMedium">
          <OperatorNetwork height={160} />
          <div className="flex flex-col items-center">
            <span className="text-[32px] text-white">Become an Operator</span>
            <div className="max-w-[251px]">
              <span className="font-aeonikRegular text-16 text-cf-light-2">
                You need a funded State Chain account to become an operator
              </span>
            </div>
          </div>
          <Button
            onClick={() => {
              if (polkadotAccount) {
                openSettingsModal();
              } else {
                openAccountModal();
              }
            }}
          >
            {polkadotAccount ? 'Get started' : 'Connect Operator'}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-y-6">
          {backPath && (
            <Link
              href={backPath}
              className="flex items-center gap-x-2 text-14 text-cf-light-2 transition hover:text-cf-white"
            >
              <ArrowBackIcon /> Back to Operators
            </Link>
          )}
          <OperatorIdPageContent
            idSs58={operatorId}
            connectedAccount={operator}
            actions={actions}
            enableHistoryExport
            redeemFunds={
              operator &&
              ((accountId) => {
                setRedemptionAccount(accountId);
                openRedeemModal();
              })
            }
            fundValidator={
              operator &&
              ((accountId) => {
                setFundingAccount(accountId);
                openFundModal();
              })
            }
            openAddToListModal={operator && openAddToListModal}
            operatorType={
              // eslint-disable-next-line no-nested-ternary
              account.nextEpoch?.operatorIdSs58 === operatorId
                ? 'upcoming'
                : account.currentEpoch?.operatorIdSs58 === operatorId
                  ? 'current'
                  : undefined
            }
            blockDelegator={operator && blockDelegator}
            allowDelegator={operator && allowDelegator}
          />
        </div>
      )}

      <ClaimValidatorModal open={claimModalOpen} onClose={closeClaimModal} />
      <ValidatorRedemptionModal
        open={redeemModalOpen}
        onClose={closeRedeemModal}
        accountId={redemptionAccount}
      />
      <RegisterNodeModal open={fundModalOpen} onClose={closeFundModal} accountId={fundingAccount} />
      <SettingsModal />
      <ConnectPolkadotWalletModal
        open={accountModalOpen}
        onClose={closeAccountModal}
        accountTypes={['operator']}
        onCompleted={(result) => {
          savePolkadotAccount(result);
          closeAccountModal();
        }}
      />
      <AddToListModal open={addToListModalOpen} onClose={closeAddToListModal} />
    </>
  );
}
